/**
 * Authenticated fetch wrapper. Mirrors the openapi-fetch middleware in
 * `./api.ts` so any hand-rolled `fetch` (history endpoint, POST /bookings,
 * notifications mark-read, etc.) shares the exact same 401 → refresh → bounce
 * pipeline. Use this instead of bare `fetch` for any call to the operator API.
 *
 *   const res = await authFetch("/api/v1/bookings/abc/history");
 *   if (!res.ok) throw new Error(...);
 *   return res.json();
 */
import { useAuthStore } from "./auth";
import { bounceToSignIn } from "./api";
import { toApiError } from "./api-error";

const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8081";

/**
 * Endpoints under `/auth/*` that don't take a Bearer token. We must
 * never recurse refresh onto these on a 401 — `/auth/refresh` would
 * loop, and the rest are public sign-in/forgot flows where 401 is the
 * actual feedback (bad credentials, bad reset token). Every OTHER
 * `/auth/*` endpoint (e.g. `/auth/email/verify/send`) is authenticated
 * and must benefit from the refresh-and-retry pipeline.
 */
function isBootstrapAuthEndpoint(url: string): boolean {
  return (
    url.includes("/auth/refresh") ||
    url.includes("/auth/sign-in") ||
    url.includes("/auth/sign-out") ||
    url.includes("/auth/forgot") ||
    url.includes("/auth/reset")
  );
}

function withBearer(init: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input.startsWith("http") ? input : baseUrl + input;
  }
  if (input instanceof URL) return input.toString();
  return input.url;
}

export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = resolveUrl(input);

  let token = useAuthStore.getState().accessToken;
  let res = await fetch(url, token ? withBearer(init, token) : init);

  // Same gating as the openapi-fetch middleware — never recurse on the
  // bootstrap auth endpoints (refresh, sign-in, etc.). All OTHER
  // `/auth/*` paths are authenticated and DO want refresh on 401.
  if (res.status === 401 && !isBootstrapAuthEndpoint(url)) {
    const refreshed = await useAuthStore.getState().tryRefresh();
    if (refreshed) {
      token = refreshed;
      res = await fetch(url, withBearer(init, refreshed));
    } else {
      bounceToSignIn();
      // Throw an error rather than returning the 401 — react-query callers
      // expect a thrown Error and we don't want them to attempt to render a
      // partial response while the redirect is in-flight.
      throw new Error("session_expired");
    }
  }

  return res;
}

/**
 * Convenience wrapper: GET the URL and parse JSON. Throws a humanized `ApiError`
 * (specific reason from the response body) on non-2xx — never a bare "HTTP 403".
 */
export async function authFetchJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<T> {
  const res = await authFetch(input, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ status: res.status }));
    throw toApiError(body);
  }
  return (await res.json()) as T;
}
